import { EDITED_AT_KEY } from "./submissionId";
import { OFFLINE_COPY, OFFLINE_UPLOAD_NOTE } from './offline';
import { uploadObject} from "./firebase";
import Toast from "./toast";
import { masterConfig, gamemodes } from "../configs/generalConfig";
import { deflate, inflate } from "pako";
import versionInfo from '../version.json';
const OSPREY_VERSION = versionInfo.version;
// import {settings,startScoutPage} from '../configs/settings'

const QR_COMPRESSED_PREFIX_B64 = "OSP2:GZ:";
const QR_COMPRESSED_PREFIX_B45 = "OSP2:GZ45:";
const QR_PATH_PACK_MARKER = "__path2b";
const QR_PATH_PACK_KEYS = new Set(["auto_field"]);
const QR_ALPHANUMERIC_REGEX = /^[0-9A-Z $%*+\-./:]+$/;
const QR_MAX_ESTIMATED_BITS = 20500;
const QR_MAX_BYTE_CHARS = 2800;
const QR_MAX_ALPHANUMERIC_CHARS = 3800;
const QR_MAX_COMPRESSED_CHARS = 3200;

const BASE45_ALPHABET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ $%*+-./:";
const BASE45_LOOKUP = BASE45_ALPHABET.split("").reduce((acc, char, index) => {
    acc[char] = index;
    return acc;
}, {});

function uint8ToBase64(bytes) {
    let binary = "";
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, i + chunkSize);
        binary += String.fromCharCode(...chunk);
    }
    return btoa(binary);
}

function base64ToUint8(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}

function uint8ToBase45(bytes) {
    let output = "";

    for (let i = 0; i < bytes.length; i += 2) {
        if (i + 1 < bytes.length) {
            const value = bytes[i] * 256 + bytes[i + 1];
            const c = value % 45;
            const d = Math.floor(value / 45) % 45;
            const e = Math.floor(value / (45 * 45));
            output += BASE45_ALPHABET[c] + BASE45_ALPHABET[d] + BASE45_ALPHABET[e];
        } else {
            const value = bytes[i];
            const c = value % 45;
            const d = Math.floor(value / 45);
            output += BASE45_ALPHABET[c] + BASE45_ALPHABET[d];
        }
    }

    return output;
}

function base45ToUint8(base45) {
    if (base45.length % 3 === 1) {
        throw new Error("Invalid base45 payload length");
    }

    const output = [];

    for (let i = 0; i < base45.length;) {
        const c0 = BASE45_LOOKUP[base45[i]];
        const c1 = BASE45_LOOKUP[base45[i + 1]];

        if (c0 === undefined || c1 === undefined) {
            throw new Error("Invalid base45 character");
        }

        if (i + 2 < base45.length) {
            const c2 = BASE45_LOOKUP[base45[i + 2]];
            if (c2 === undefined) {
                throw new Error("Invalid base45 character");
            }

            const value = c0 + c1 * 45 + c2 * 45 * 45;
            if (value > 0xffff) {
                throw new Error("Invalid base45 chunk value");
            }

            output.push(Math.floor(value / 256));
            output.push(value % 256);
            i += 3;
        } else {
            const value = c0 + c1 * 45;
            if (value > 0xff) {
                throw new Error("Invalid base45 tail value");
            }
            output.push(value);
            i += 2;
        }
    }

    return new Uint8Array(output);
}

function tryEncodePathPoints(points) {
    if (!Array.isArray(points)) return null;

    const buffer = new Uint8Array(points.length * 2);

    for (let i = 0; i < points.length; i++) {
        const raw = points[i];
        let x;
        let y;

        if (typeof raw === "string") {
            const split = raw.split(",");
            if (split.length !== 2) return null;
            x = Number(split[0]);
            y = Number(split[1]);
        } else if (raw && typeof raw === "object") {
            x = Number(raw.x);
            y = Number(raw.y);
        } else {
            return null;
        }

        if (!Number.isInteger(x) || !Number.isInteger(y) || x < 0 || x > 255 || y < 0 || y > 255) {
            return null;
        }

        buffer[i * 2] = x;
        buffer[i * 2 + 1] = y;
    }

    return uint8ToBase64(buffer);
}

function tryDecodePathPoints(encodedPath) {
    if (typeof encodedPath !== "string") return null;

    const buffer = base64ToUint8(encodedPath);
    if (buffer.length % 2 !== 0) return null;

    const points = [];
    for (let i = 0; i < buffer.length; i += 2) {
        points.push(`${buffer[i]},${buffer[i + 1]}`);
    }
    return points;
}

function restoreEntryValueFromQR(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) return value;
    if (typeof value[QR_PATH_PACK_MARKER] !== "string") return value;

    try {
        const decodedPath = tryDecodePathPoints(value[QR_PATH_PACK_MARKER]);
        return decodedPath || value;
    } catch {
        return value;
    }
}

function getPackedQRData(map) {
    if (!(map instanceof Map)) return getCurrentData(map);

    const packedEntries = [...map.entries()].map(([key, value]) => {
        if (!QR_PATH_PACK_KEYS.has(key)) return [key, value];

        const encodedPath = tryEncodePathPoints(value);
        if (!encodedPath) return [key, value];

        return [key, { [QR_PATH_PACK_MARKER]: encodedPath }];
    });

    return JSON.stringify(packedEntries);
}

function estimateQrBitCost(payload) {
    if (QR_ALPHANUMERIC_REGEX.test(payload)) {
        return Math.ceil(payload.length / 2) * 11;
    }
    return payload.length * 8;
}

function pickBestPayloadByQrCost(candidates) {
    if (!Array.isArray(candidates) || candidates.length === 0) return "";

    let bestPayload = candidates[0];
    let bestCost = estimateQrBitCost(bestPayload);

    for (let i = 1; i < candidates.length; i++) {
        const candidate = candidates[i];
        const candidateCost = estimateQrBitCost(candidate);

        if (candidateCost < bestCost || (candidateCost === bestCost && candidate.length < bestPayload.length)) {
            bestPayload = candidate;
            bestCost = candidateCost;
        }
    }

    return bestPayload;
}

function buildCompressedCandidates(plain, packed) {
    const candidates = [];

    try {
        const compressedPlain = deflate(plain);
        candidates.push(QR_COMPRESSED_PREFIX_B64 + uint8ToBase64(compressedPlain));
        candidates.push(QR_COMPRESSED_PREFIX_B45 + uint8ToBase45(compressedPlain));

        if (packed !== plain) {
            const compressedPacked = deflate(packed);
            candidates.push(QR_COMPRESSED_PREFIX_B64 + uint8ToBase64(compressedPacked));
            candidates.push(QR_COMPRESSED_PREFIX_B45 + uint8ToBase45(compressedPacked));
        }
    } catch (err) {
        console.warn("Failed to compress QR payload candidates:", err);
    }

    return candidates;
}

function tryParseDataMapString(payload) {
    const parsed = JSON.parse(payload);

    if (Array.isArray(parsed)) {
        const maybeEntries = parsed.every((entry) => Array.isArray(entry) && entry.length >= 2);
        if (!maybeEntries) return null;

        const restoredEntries = parsed.map(([key, value]) => [key, restoreEntryValueFromQR(value)]);
        return new Map(restoredEntries);
    }

    if (parsed && typeof parsed === "object") {
        const restoredEntries = Object.entries(parsed).map(([key, value]) => [key, restoreEntryValueFromQR(value)]);
        return new Map(restoredEntries);
    }

    return null;
}
/**
 * Save a dataMap for a specific match
 * @param {int} matchId match number
 * @param {Map} dataMap 
 * @param {int} gamemodeNum the int that represents the gamemode (0-3) (quals/elims/etc.)
 */
export function saveDataMap(matchId, dataMap, gamemodeNum) {
    let uploadMode = gamemodes[gamemodeNum]
    localStorage.setItem(`${uploadMode}_match_${matchId}`, getCurrentData(dataMap));
}

/**
 * Loads a dataMap by match ID from localstorage
 * @param {int} matchId the match number
 * @param {int} gamemodeNum the int that represents the gamemode (0-3) (quals/elims/etc.)
 * @returns {Map} the dataMap for that match
 */
export function loadDataMap(matchId, gamemodeNum) {
    let uploadMode = gamemodes[gamemodeNum]
    const raw = localStorage.getItem(`${uploadMode}_match_${matchId}`);
    if (!raw) return null;
    return new Map(JSON.parse(raw)); // parse back into entries
}

/**
 * Gets all matchs that are currently saved in localstorage
 * idk why this is here when i have the other one to get the numbers
 * just for funsies ig
 * @param {int} gamemodeNum the int that represents the gamemode (0-3) (quals/elims/etc.)
 * @returns {Array<string>} array, data looks like this: '{type}_match_#'
 */
export function getAllSavedMatches(gamemodeNum) {
    let uploadMode = gamemodes[gamemodeNum]
    const matches = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key.startsWith(`${uploadMode}_match_`)) { // only selects match data
            const data = (localStorage.getItem(key));
            matches.push(data);
        }
    }
    return matches;
}
/**
 * Gets all match numbers that are currently saved in localstorage
 * @param {int} gamemodeNum the int that represents the gamemode (0-3) (quals/elims/etc.)
 * @returns {Array<int>} array
 */
export function getAllSavedMatchNumbers(gamemodeNum) {
    let uploadMode = gamemodes[gamemodeNum]

    const matches = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key.startsWith(`${uploadMode}_match_`)) { // only selects match data for the gametype
            matches.push(key.substring(`${uploadMode}_match_`.length));
        }
    }
    return matches;
}
/**
 * Copy a string to clipboard
 * @param {String} string the string to copy 
 */
export function copyString(string){
    navigator.clipboard.writeText(string);
    Toast.info("Copied text to clipboard");
}

/**
 * Deletes a match from localstorage
 * @param {string} matchId this should be just the number of the match
 */
/**
 * Deletes a saved match. Saved copies are keyed by game mode (`Quals_match_12`, see
 * saveDataMap); without one this used to remove `match_12`, a key nothing writes, so
 * Clear Data cleared nothing. The old key is still removed for anything that has it.
 */
export function deleteDataMap(matchId, gamemodeNum) {
    if (gamemodeNum !== undefined && gamemodes[gamemodeNum] !== undefined) {
        localStorage.removeItem(`${gamemodes[gamemodeNum]}_match_${matchId}`);
    }
    localStorage.removeItem(`match_${matchId}`);
}

/**
 * Returns the JSON string of the inputed map
 * @param {Map} map map 
 * @returns {string} the stringified map
 */
export function getCurrentData(map){
    let stuff = JSON.stringify(map, (key, value) => (value instanceof Map ? [...value] : value));
    //console.log(stuff);
    return stuff;
}

/**
 * Returns a compressed QR payload string when possible.
 * Falls back to packed/plain text if compression is unavailable.
 * @param {Map} map
 * @returns {string}
 */
export function getCompressedQRData(map) {
    const plain = getCurrentData(map);
    const packed = getPackedQRData(map);

    const compressedCandidates = buildCompressedCandidates(plain, packed);
    if (compressedCandidates.length > 0) {
        return pickBestPayloadByQrCost(compressedCandidates);
    }

    // Compression failed; return the smaller non-compressed fallback.
    return pickBestPayloadByQrCost([plain, packed]);
}

/**
 * Returns the best QR-safe payload string.
 * Prefers whichever option is most QR-efficient: plain, packed, or compressed variants.
 * @param {Map} map
 * @returns {string}
 */
export function getCurrentQRData(map) {
    const plain = getCurrentData(map);
    const packed = getPackedQRData(map);

    const candidates = [plain];
    if (packed !== plain) {
        candidates.push(packed);
    }

    const compressedCandidates = buildCompressedCandidates(plain, packed);
    candidates.push(...compressedCandidates);

    return pickBestPayloadByQrCost(candidates);
}

/**
 * Conservative capacity check to avoid QR render failures for oversized payloads.
 * @param {string} payload
 * @returns {boolean}
 */
export function canGenerateQRCodePayload(payload) {
    if (typeof payload !== "string") return false;
    const normalized = payload.trim();
    if (normalized.length === 0) return false;

    const isCompressedPayload = normalized.startsWith(QR_COMPRESSED_PREFIX_B64) || normalized.startsWith(QR_COMPRESSED_PREFIX_B45);
    const isAlphanumericPayload = QR_ALPHANUMERIC_REGEX.test(normalized);

    if (isCompressedPayload && normalized.length > QR_MAX_COMPRESSED_CHARS) {
        return false;
    }

    if (isAlphanumericPayload && normalized.length > QR_MAX_ALPHANUMERIC_CHARS) {
        return false;
    }

    if (!isAlphanumericPayload && normalized.length > QR_MAX_BYTE_CHARS) {
        return false;
    }

    return estimateQrBitCost(normalized) <= QR_MAX_ESTIMATED_BITS;
}

/**
 * Decodes either the new compressed QR payload format or legacy plain JSON format.
 * @param {string} payload
 * @returns {Map|null}
 */
export function decodeDataMapPayload(payload) {
    if (typeof payload !== "string") return null;
    const raw = payload.replace(/\r/g, "");
    if (raw.length === 0 || raw.trim().length === 0) return null;

    try {
        if (raw.startsWith(QR_COMPRESSED_PREFIX_B45)) {
            const base45 = raw.slice(QR_COMPRESSED_PREFIX_B45.length);
            const bytes = base45ToUint8(base45);
            const decompressed = inflate(bytes, { to: "string" });
            return tryParseDataMapString(decompressed);
        }

        if (raw.startsWith(QR_COMPRESSED_PREFIX_B64)) {
            const base64 = raw.slice(QR_COMPRESSED_PREFIX_B64.length);
            const bytes = base64ToUint8(base64);
            const decompressed = inflate(bytes, { to: "string" });
            return tryParseDataMapString(decompressed);
        }

        return tryParseDataMapString(raw.trim());
    } catch (err) {
        console.error("Failed to decode payload:", err);
        return null;
    }
}

/**
 * Checks if the attempted upload is valid
 * @param {Object} dataObject 
 * @returns {boolean}
 */
export function isValidData(dataObject, settingsYear) {
    if(!dataObject || typeof(dataObject) !== "object"){
        Toast.error("Invalid data object - Please Contact a Narwhal for assistance");
        return false;
    }
    const message = dataObject["scout_id"] + " from " + dataObject["scout_team"] + " - Match " + dataObject["match_number"] + " - Robot " + dataObject["team_number"];

    let startScoutPages = {...masterConfig[settingsYear].startScoutPage}
    let settings = {...masterConfig[settingsYear].mainSettings}

    if (!isNaN(dataObject["match_number"])) {
        const value = parseInt(dataObject["match_number"]);
        if(isNaN(parseInt(dataObject["match_number"]))){
            Toast.error("Invalid match_number - "+ message);
            return false;
        }
        else if (value < 1 || value > parseInt(startScoutPages.match_number.maxLength)) {
            Toast.error("Invalid match_number - "+ message);
            return false;
        }
    }
    else {
        Toast.error("Invalid match_number - "+ message);
        return false;
    }
    if (!isNaN(dataObject["team_number"])) {
        const value = parseInt(dataObject["team_number"]);
        if(isNaN(parseInt(dataObject["team_number"]))){
            Toast.error("Invalid team_number - "+ message);
            return false;
        }
        else if (value < 1 || value > 12000) {
            Toast.error("Invalid team_number - "+ message);
            return false;
        }
    }
    else {
       Toast.error("Invalid team_number - "+ message);
        return false;
    }
    if (!isNaN(dataObject["team_position"])) {
        const value = parseInt(dataObject["team_position"]);
        if(isNaN(parseInt(dataObject["team_position"]))){
            Toast.error("Invalid team_position - "+ message);
            return false;
        }
        if (value < 1 || value > 3) {
            Toast.error("Invalid team_position - "+ message);
            return false;
        }
    }
    else {
        Toast.error("Invalid team_position - "+ message);
        return false;
    }
    if (dataObject["alliance_color"] !== null) {
        const value = dataObject["alliance_color"];
        if(value != "r" && value != "b"){
            Toast.error("Invalid alliance_color - "+ message);
            return false;
        }
    }
    else{
        Toast.error("Invalid alliance_color - "+ message);
        return false;
    }

    const expectedVersion = OSPREY_VERSION;
    if (!expectedVersion) {
        // version.json is committed, so this should never be empty in practice - kept as a
        // defensive fallback so a malformed/blank file doesn't fail every single upload.
        console.warn("version.json has no version set - skipping version check.");
    }
    else if (dataObject["version"] === null) {
        Toast.error("Invalid version - Please Contact a Narwhal for assistance");
        return false;
    }
    else if ((dataObject["version"])) {
        const value = String(dataObject["version"]);
        if (!(value.includes(expectedVersion.substring(1,4)))) {
            Toast.error("Invalid version - Please Contact a Narwhal for assistance");
            return false;
        }
    }
    return true; //remove this later, this is skipping the upload requirments so just dont be stupid when uploading


    for (const data of [settings.start, settings.auto, settings.tele, settings.after]){
        for (let i = 0; i < data.length; i++) {
            const datapoint = data[i];
            if(!(dataObject.hasOwnProperty(datapoint.label))){
                Toast.error("Invalid Input. " + datapoint.label + " not found - "+ message);
                return false;
            } //if the datapoint isnt in dataobj then continue
            switch (datapoint.writeType) {
                case "str":
                    break;
                case "bool":
                    if( (/^(true|false)$/i.test(dataObject[datapoint.label])) === false){
                        Toast.error("Invalid " + datapoint.label + " - "+ message);
                        return false;
                    }
                    break;
                case "cycG":
                    break;
                case "tog":
                    break;
                default: //for ints
                    if( (/^\d+$/.test(dataObject[datapoint.label])) === false){
                        Toast.error("Invalid " + datapoint.label + " - "+ message);
                        return false;
                    }
                    break;
            }
        }
    }

    let properSize = settings.start.length + settings.auto.length + settings.tele.length + settings.after.length + 1; // +1 is for version
    // An edited copy carries when it was edited; that is bookkeeping, not a datapoint.
    const actualSize = Object.keys(dataObject).filter(key => key !== EDITED_AT_KEY).length
    if( actualSize !== properSize){
        let mes = "";
        console.log("actual", actualSize)
        console.log("proper", properSize)
        if(actualSize > properSize) mes = "Too many";
        else if(actualSize < properSize) mes = "Not enough";
        Toast.error("Invalid JSON Length. " + mes + " datapoints - " + message)
        return false;
    }

    return true;
}

/**
 * Checks if upload has correct permissions
 * @param {*} currentUser 
 * @param {boolean} authorized authorization status
 * @param {String} dataMap map of the data
 * @param {String} eventName the eventpath
 * @param {Array<String>} eventList
 * @param {boolean} onlineStatus
 * @param {int} settingsYear
 * @returns 
 */
export function basicUploadChecks(currentUser,authorized,dataMap,eventName,eventList, onlineStatus, settingsYear){
    const reason = uploadBlocker(currentUser,authorized,dataMap,eventName,eventList, onlineStatus, settingsYear);
    if (reason) {
        Toast.error(reason);
        return false;
    }
    return true;
}

/**
 * Why a Firebase upload can't happen right now, or null when it can. The same checks as
 * basicUploadChecks without the toast, for Submit, which saves and exports regardless and
 * only mentions why it didn't upload.
 * @returns {string|null}
 */
export function uploadBlocker(currentUser,authorized,dataMap,eventName,eventList, onlineStatus, settingsYear){
    // The offline copy never uploads (lib/offline.js): Submit, Relay and the Scanner all stop here.
    if (OFFLINE_COPY) return OFFLINE_UPLOAD_NOTE;
    if(onlineStatus === false) return "No Internet Connection: Upload Not Registered";
    if (!navigator.onLine) return "No Wifi Connection: Upload Not Registered";
    if (currentUser == null) return "Not signed in. Please sign in to Upload. Ensure that account is authorized.";
    if (!authorized) return "Account Not Authorized: Upload Not Registered";
    if (dataMap == "") return "Empty Push: Upload Not Registered";
    if (!eventName) return "No Event Path entered: Input a valid Event Path to upload";
    if (eventList && eventList.size > 0 && !eventList.has(eventName)) return "Event Path Not Found in Database";
    if(parseInt(eventName.substring(0,4)) !== parseInt(settingsYear) && parseInt(eventName.substring(0,4)) != 9998 && parseInt(eventName.substring(0,4)) != 9999){
        return "Event Year Doesn't Match With Database Game";
    }
    return null;
}

/**
 * Initiates process to upload data to firebase
 * @param {*} currentUser 
 * @param {boolean} authorized authorization status
 * @param {String} dataMap map of the data
 * @param {String} eventName the eventpath
 * @param {boolean} onlineStatus
 * @param {int} settingsYear
 * @param {int} gamemode 
 * @returns 
 */
export async function uploadData(currentUser, authorized, dataMap,eventName,eventList, onlineStatus, settingsYear, gamemode, options = {}) {

    const bob = basicUploadChecks(currentUser,authorized,dataMap,eventName,eventList, onlineStatus, settingsYear);
    if(bob === false) return false;

    if(gamemode === undefined) return false;


    let dataObject = formatData(dataMap);

    if(dataObject === false) return;

    const outcome = isValidData(dataObject, settingsYear)
    if (outcome === false) {
        return false;
    } else {
        let rengoku = await uploadObject(dataObject,eventName, gamemode, options);
        return rengoku;
    }
}

/**
 * Formats the data from the dataMap into a clean dataObject
 * @param {Map} dataMap 
 * @returns 
 */
export function formatData(dataMap) {
    let dataObject;
    try {
        dataObject = Object.fromEntries(dataMap)
    } catch (err) {
        Toast.error("Invalid Map Format");
        return false;
    }
    const newObj = {};
    for (const [key, value] of Object.entries(dataObject)) {
        newObj[key.toLowerCase().replace(/\s/g, "").trim()] = value;
    }
    return newObj;
}